import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useImperativeHandle,
} from "react";
import { createPortal } from "react-dom";
import styled, { css, keyframes } from "styled-components";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";

import IconButton from "../button/IconButton";
import ToolTip from "../tooltip/ToolTip";
import {
  getFileIconFromCategoryOrExtension,
  getFileIconFromMime,
} from "../file-icon/fileIcon";
import MarkdownHelpModal from "./MarkdownHelpModal";
import {
  useTransformTextMutation,
  AITextScene,
} from "../../app/services/aiText";
import {
  useSpeechRecognition,
  SpeechRecognitionErrorReason,
} from "../../hooks/useSpeechRecognition";
import type { FileWithPath } from "../../hooks/useS3Upload";
import { isMacLike } from "../../utils/keyboardShortcuts";
import { useFileDropTarget } from "./useFileDropTarget";

// 固定の高さ値を定数として定義（コンポーネント外で定義して再レンダリング時の再計算を防ぐ）
const BOTTOM_APP_BAR_HEIGHT = 80; // BottomAppBarの高さ（ピクセル）
const GLOBAL_MENU_HEIGHT = 64; // GlobalMenuの高さ（ピクセル）
const PROJECT_BANNER_HEIGHT = 83; // ProjectBannerの高さ（ピクセル）
const CONTAINER_PADDING = 8 * 2; // 上下のパディング
const CONTAINER_BORDER = 1 * 2; // 上下のボーダー
const BUTTON_AREA_HEIGHT = 48; // 送信ボタンエリアの高さ
const MIN_TEXTAREA_HEIGHT = 100; // テキストエリアの最小高さ

// 行の高さを定数として定義
const LINE_HEIGHT_FACTOR = 1.5; // line-height: 150%に対応
const FONT_SIZE_PX = 16; // 基本フォントサイズ（ピクセル）
const LINE_HEIGHT_PX = FONT_SIZE_PX * LINE_HEIGHT_FACTOR; // 1行の高さ（ピクセル）

// ライブタイムコード（getLiveTimecode）の追従更新間隔（ミリ秒）
const LIVE_TIMECODE_INTERVAL_MS = 250;

// 送信ショートカットの表記。判定側は ⌘ / Ctrl の両方を受けるので表記だけの出し分け。
const SEND_SHORTCUT_LABEL = `${isMacLike ? "⌘" : "Ctrl"} + Enter`;

// 本文先頭のタイムコードタグ（例 "[1:23]" / "[1:23.12]" / 絶対フレーム "[300]"）の判定用
const BRACKET_TIMECODE_HEAD_RE = /^\s*\[\d+(:\d{2}(\.\d+)?)?\]/;

// 音声入力のエラー文言。無音タイムアウト（no-speech）は自然に起こるので通知しない。
const DICTATION_ERROR_MESSAGES: Partial<
  Record<SpeechRecognitionErrorReason, string>
> = {
  "not-allowed":
    "マイクの使用が許可されていません。ブラウザの設定から許可してください",
  "audio-capture": "マイクが見つかりませんでした",
  network: "音声認識に接続できませんでした",
  unknown: "音声入力を開始できませんでした",
};

// ブラウザがクリップボード画像に付ける汎用名（空 or "image.png" 等）。これに
// 当てはまるものだけ改名し、Finder などからコピーした実ファイルは元名を保つ。
const GENERIC_CLIPBOARD_NAME_RE = /^(image\.[a-z0-9]+)?$/i;

/** 拡張子（ドットなし）を、ファイル名 → MIME サブタイプの順に解決する。 */
const resolveExtension = (file: File): string =>
  file.name.match(/\.([a-z0-9]+)$/i)?.[1] ??
  file.type.match(/^\w+\/([a-z0-9]+)/i)?.[1] ??
  "bin";

/** ファイル名に使える `YYYYMMDD-HHMMSS` 形式。 */
const formatFileNameTimestamp = (date: Date): string => {
  const pad = (value: number) => String(value).padStart(2, "0");
  return (
    `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}` +
    `-${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`
  );
};

/**
 * 貼り付けたファイルに一意な名前を与える。名前が汎用名のままだと、
 * 添付一覧でスクリーンショットが全て "image.png" になって区別できない。
 *
 * `index` は同時に複数枚貼ったときの連番（2枚目以降に付与）。
 */
const withPastedFileName = (file: File, index: number): File => {
  if (!GENERIC_CLIPBOARD_NAME_RE.test(file.name)) return file;

  const stamp = formatFileNameTimestamp(new Date());
  const sequence = index > 0 ? `-${index + 1}` : "";

  return new File(
    [file],
    `pasted-${stamp}${sequence}.${resolveExtension(file)}`,
    { type: file.type, lastModified: file.lastModified },
  );
};

/**
 * 音声入力セッションの挿入状態。
 *
 * 未確定分は毎回 `base` の `offset` 位置へ差し込んで表示するだけなので、
 * 確定前に何度描き替えても本文には残らない。
 */
interface DictationInsertion {
  /** 確定済みテキストだけを反映した本文（認識中の未確定分は含まない） */
  base: string;
  /** base 内の挿入位置。確定するたびにその長さ分だけ進む */
  offset: number;
}

// 返信対象メッセージの型定義
interface ReplyToMessage {
  id: string;
  author: {
    display_name: string;
  };
  message: string;
}

// 編集時に表示する既存添付ファイルの型
export interface ExistingAttachedFile {
  id: string;
  fileName: string;
  fileType: string;
}

// AI 支援（チクチクフィルター / 返信君）の送信メタ。onSend の第2引数で渡る。
// original_comment=元/自分の文、ai_comment=校正案/返信案、active_variant=送信時にアクティブな側。
export interface AiAssistSendMeta {
  original_comment?: string | null;
  ai_comment?: string | null;
  active_variant?: "original" | "ai" | null;
}

// 編集モード時に両バージョンを復元するための初期値（BE の proofread と対応）。
export interface CommentFieldProofread {
  original: string;
  corrected: string;
  active: "original" | "ai";
}

// チクチクフィルター / 返信君が文脈解決に使う識別子（FE は識別子のみ送り、文脈は BE が組み立てる）。
export interface ChikuchikuContext {
  project_id?: string;
  shot_id?: string | null;
  /** DM スレッドのメッセージ履歴を文脈に含めるための識別子 */
  dm_id?: string | null;
  /** Chat（オファー非依存 DM）スレッドのメッセージ履歴を文脈に含めるための識別子 */
  chat_id?: string | null;
  /** 編集時、自分自身のコメント/メッセージを履歴から除外する */
  exclude_comment_id?: string | null;
}

// 返信君のコンテキストはチクチクフィルターと同形（project_id / shot_id / dm_id / exclude_comment_id）。
export type ReplyAssistantContext = ChikuchikuContext;

/**
 * 外部（キーボードショートカット等）から入力欄を操作するための imperative handle。
 * React 19 の ref-as-prop（`ref` prop）経由で公開する。
 */
export interface CommentFieldHandle {
  /**
   * コメント入力欄へフォーカスする（カーソルは末尾）。
   * `withPin: true` のときは、ピンが未アクティブならピンボタンのクリックと
   * 同じ処理（onPinClick の戻り値のタイムコードを本文先頭へ挿入）も行う。
   * タイムラインモード（ピン常時 ON = isPinActive）ではフォーカスのみ。
   */
  focusComposer: (options?: { withPin?: boolean }) => void;
}

// 型定義
interface CommentFieldProps {
  /** 外部から入力欄を操作するための ref（CommentFieldHandle） */
  ref?: React.Ref<CommentFieldHandle>;
  showPinIcon?: boolean;
  showAttachIcon?: boolean;
  /**
   * 送信ハンドラ。Promise が `false` を解決した場合は「送信キャンセル」と
   * みなし、ユーザーが再入力していなければ本文を復元する
   * （添付アップロード失敗ダイアログでキャンセルした場合等）。
   */
  onSend?: (
    message: string,
    meta?: AiAssistSendMeta,
  ) => void | boolean | Promise<void | boolean>;
  showBottomAppBar?: boolean;
  scrollEnabled?: boolean;
  // 編集モード用のprops
  editMode?: boolean;
  initialValue?: string;
  onCancel?: () => void;
  // 返信モード用のprops
  replyMode?: boolean;
  replyToMessage?: ReplyToMessage | null;
  // ファイル選択用のprops
  onFilesSelected?: (files: File[]) => void;
  /**
   * 入力欄へのドロップでフォルダ構造ごと受け取るためのコールバック。
   * 指定するとドロップ分はこちらへ渡る（未指定なら onFilesSelected へ平坦化して渡す）。
   * 背後のメッセージ一覧に置いた DropZoneOverlay の `onDropWithPath` と同じものを渡す想定。
   */
  onFilesDroppedWithPath?: (filesWithPath: FileWithPath[]) => void;
  selectedFiles?: File[];
  onRemoveFile?: (index: number) => void;
  /** ファイル選択を許可するかどうか（ファイル選択ダイアログの表示制御） */
  acceptFileTypes?: string;
  /** 複数ファイル選択を許可するか */
  multipleFiles?: boolean;
  // ピン機能用のprops
  /** ピンアイコンクリック時のコールバック。文字列を返すとテキスト先頭に挿入される */
  onPinClick?: () => string | void;
  /** ピンがアクティブかどうか（アイコンの色を変更） */
  isPinActive?: boolean;
  /** ピンアイコンのツールチップ（例: "バリエーションAにコメント"） */
  pinTooltip?: string;
  /**
   * 再生位置のタイムコード（例 "[1:23.12]"）を返すコールバック。
   * 指定すると「ピン常時 ON」モードになる（タイムラインモード用）:
   *  - ピンアイコン隣に再生位置へ追従するタイムコードチップを表示する
   *    （入力を始めると凍結し、クリア / 送信で追従に戻る。ピンクリックで
   *    凍結中の値を現在位置に取り直す）
   *  - 送信時、本文先頭にタイムコードが無ければチップの値を自動付与する
   *  - onPinClick によるトグル / テキスト挿入は行わない
   */
  getLiveTimecode?: () => string | null;
  /** 編集モード時もピン・アップロードアイコンを表示するか */
  showIconsInEditMode?: boolean;
  /** 編集時に表示する既存添付ファイル一覧 */
  existingFiles?: ExistingAttachedFile[];
  /** 既存添付ファイル削除時のコールバック */
  onRemoveExistingFile?: (fileId: string) => void;
  /** 通常時のプレースホルダー（デフォルト: "メッセージを送信"） */
  placeholder?: string;
  /**
   * 入力中（Typing）変化時に呼ばれる。useTypingSender の `onTypingChange` を
   * そのまま渡す想定。
   */
  onTypingChange?: () => void;
  /**
   * 入力中（Typing）確定時に呼ばれる（blur / 送信成功）。
   * useTypingSender の `onTypingCommit` をそのまま渡す想定。
   */
  onTypingCommit?: () => void;
  // ===== チクチクフィルター（AI 校正）用 props =====
  /** チクチクフィルター（テキストエリア上のアイコン + 元/校正トグル）を有効化する */
  enableChikuchiku?: boolean;
  /** 使用するシーンキー（既定: "chikuchiku_filter"） */
  chikuchikuScene?: AITextScene;
  /** 文脈解決に使う識別子（project_id は必須、shot_id 等は任意） */
  chikuchikuContext?: ChikuchikuContext;
  /** 編集モード時に元/校正の両バージョンを復元するための初期値 */
  initialProofread?: CommentFieldProofread | null;
  // ===== 返信君（AI 返信生成）用 props =====
  /** 返信君（テキストエリア上のボタン + 自分の文/返信案トグル）を有効化する */
  enableReplyAssistant?: boolean;
  /** 使用するシーンキー（例: "reply_assistant_shot"） */
  replyAssistantScene?: AITextScene;
  /** 文脈解決に使う識別子（project_id は必須、shot_id / dm_id 等は任意） */
  replyAssistantContext?: ReplyAssistantContext;
  /**
   * 外部条件（例: 比較グループでメディア選択済み）により、テキスト・添付が
   * 空でも送信ボタンを有効化する。onSend には空文字が渡る。
   */
  allowEmptySend?: boolean;
}

interface RootState {
  layout: string;
  router?: {
    location?: {
      pathname: string;
    };
    routes?: Array<{
      path: string;
      isHideTopBar?: boolean;
    }>;
  };
}

// Styled components for CommentField
const CommentFieldContainer = styled.div.attrs({
  className: "u-w100 u-col u-gap8",
})`
  border-radius: 6px;
  border: 1px solid #f0f0f0;

  max-width: 740px;

  /* From https://css.glass */
  background: rgba(255, 255, 255, 0.16);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(13px);
  -webkit-backdrop-filter: blur(13px);

  padding: 8px 12px;
`;

// テキストエリア用のスタイル
const StyledTextarea = styled.textarea.attrs({
  className: "u-w100 u-line-height-150",
})`
  resize: none;
  overflow-y: auto;
  font-size: ${FONT_SIZE_PX}px;
  line-height: ${LINE_HEIGHT_FACTOR};

  /* スクロールイベントの伝播を確保 */
  &:focus {
    outline: none;
  }

  /* モバイル表示時のスクロールバー制御 */
  @media (max-width: 768px) {
    &:not(:focus) {
      overflow-y: hidden;
    }

    /* フォーカス時にスクロールバーを表示するが、必要ない場合は非表示 */
    &:focus {
      overflow-y: auto;
      scrollbar-width: thin;

      /* スクロールが不要な場合はスクロールバーを非表示 */
      &:not(.needs-scroll) {
        &::-webkit-scrollbar {
          width: 0;
          background: transparent;
        }
        scrollbar-width: none;
      }
    }
  }

  /* スクロールイベントの伝播を確保するための追加スタイル */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }
`;

// MarkdownHelpIcon / 送信アイコンの個別カラー指定は IconButton の `color`
// `hoverColor` `size` props で直接行うようにし、styled-components の wrap を
// 廃止した（旧実装で specificity を上げるために `&&` を使っていた箇所も解消）。

// 編集モードラベル用のスタイル（点滅アニメーション追加）
const EditModeLabel = styled.div.attrs({
  className: "wobble-hor-bottom",
})`
  font-size: 11px;
  padding: 4px 16px;
  color: var(--soremo-blue);
  border: 1px solid var(--soremo-blue);
`;

// 返信モードラベル用のスタイル
const ReplyModeLabel = styled.div.attrs({
  className: "wobble-hor-bottom",
})`
  font-size: 11px;
  padding: 4px 16px;
  color: var(--soremo-blue);
  border: 1px solid var(--soremo-blue);
`;

// 返信プレビューコンテナ
const ReplyPreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  background-color: rgba(0, 0, 0, 0.02);
  border-left: 3px solid var(--soremo-light-gray);
  border-radius: 0 4px 4px 0;
`;

// 返信先の投稿者名
const ReplyAuthorName = styled.span.attrs({
  className: "label8",
})`
  color: var(--soremo-blue);
`;

// 返信先のメッセージプレビュー
const ReplyMessagePreview = styled.span.attrs({
  className: "bodytext-11 u-line-height-150",
})`
  color: var(--soremo-light-gray);
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

/**
 * ライブタイムコードチップ（タイムラインモードのピン常時 ON 用）。
 * 追従中はグレー、凍結中（入力開始後）は soremo-blue で表示する。
 * 凍結中はクリックで現在の再生位置へ取り直せる（ピンアイコンは
 * タイムラインモードでは非表示のため、チップ自身が操作点になる）。
 */
const TimecodeChip = styled.span.attrs({
  className: "label8",
})<{ $frozen: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid
    ${({ $frozen }) => ($frozen ? "var(--soremo-blue)" : "#f0f0f0")};
  color: ${({ $frozen }) =>
    $frozen ? "var(--soremo-blue)" : "var(--soremo-light-gray)"};
  background: rgba(255, 255, 255, 0.6);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  cursor: ${({ $frozen }) => ($frozen ? "pointer" : "default")};
`;

// 選択ファイルプレビューコンテナ
const SelectedFilesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
`;

// 選択ファイルアイテム
const SelectedFileItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background-color: var(--soremo-background);
  border-radius: 4px;
  border: 1px solid #f0f0f0;
`;

// ファイルアイコン
const FileIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})`
  font-size: 16px;
  color: var(--soremo-light-gray);
`;

// 画像添付のサムネイル（アイコンと同じ座を占めるので寸法を揃える）
const FileThumbnail = styled.img`
  width: 16px;
  height: 16px;
  border-radius: 2px;
  object-fit: cover;
  flex-shrink: 0;
`;

// ファイル名
const SelectedFileName = styled.span.attrs({
  className: "bodytext-11",
})`
  color: var(--soremo-gray);
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// ファイル削除ボタン
const RemoveFileButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--soremo-light-gray);
  transition: color 0.2s;

  &:hover {
    color: var(--soremo-blue);
  }
`;

// ファイル削除アイコン
const RemoveFileIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})`
  font-size: 16px;
`;

// 非表示のファイル入力
const HiddenFileInput = styled.input`
  display: none;
`;

/**
 * ドラッグ中にコメント入力欄へ重ねるハイライト。
 * 背後のメッセージ一覧（DropZoneOverlay）だけが反応する状態だと、添付先が
 * この入力欄だと分かりづらいため、入力欄側にも受け口を明示する。
 *
 * ページ側オーバーレイ（z-index: 1000）より前面に出す必要があるが、入力欄は
 * 固定パネルやポータルの中にあって祖先の重なり順に縛られる。そのため body 直下へ
 * ポータルし、計測した矩形へ position: fixed で重ねる（位置は呼び出し側が style で渡す）。
 */
const DropHighlight = styled.div.attrs({
  className: "u-row-center u-gap8",
})<{ $active: boolean }>`
  position: fixed;
  z-index: 1001;
  border-radius: 6px;
  border: 2px dashed var(--soremo-blue);
  background-color: rgba(233, 246, 252, 0.94);
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  visibility: ${({ $active }) => ($active ? "visible" : "hidden")};
  transition:
    opacity 0.15s ease,
    visibility 0.15s ease;
  /* 重なった瞬間に dragleave が飛んでちらつくため、イベントは常に透過させる */
  pointer-events: none;
`;

const DropHighlightIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})`
  font-size: 24px;
  color: var(--soremo-blue);
`;

const DropHighlightText = styled.span.attrs({
  className: "bodytext-11",
})`
  color: var(--soremo-blue);
`;

const dictationPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
`;

// 音声入力中のマイクアイコン。録音中であることが一目で分かるよう脈打たせる。
const DictationIndicator = styled.span<{ $active: boolean }>`
  display: inline-flex;

  ${({ $active }) =>
    $active &&
    css`
      animation: ${dictationPulse} 1.4s ease-in-out infinite;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    `}
`;

// AI 支援行（チクチクフィルター / 返信君。テキストエリア直上）
const AiAssistBar = styled.div.attrs({
  className: "u-row u-gap8",
})`
  align-items: center;
  flex-wrap: wrap;
`;

// 生成前: AI 支援の起動チップ（チクチクフィルター起動 / 返信君起動）
const AiAssistTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid var(--soremo-border);
  border-radius: 999px;
  background: transparent;
  color: var(--soremo-blue);
  padding: 4px 10px;
  font-size: 11px;
  line-height: 1;
  cursor: pointer;
  transition:
    background 0.15s ease,
    opacity 0.15s ease;

  &:hover:not(:disabled) {
    background: rgba(0, 154, 206, 0.08);
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
  & > .material-symbols-rounded {
    font-size: 16px;
  }
`;

// 生成後: 元/AI 案の切り替えトグル（元の文⇄校正案 / 自分の文⇄返信案）
const VariantToggle = styled.div`
  display: inline-flex;
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  overflow: hidden;
`;

const VariantButton = styled.button<{ $active: boolean }>`
  border: none;
  cursor: pointer;
  padding: 4px 12px;
  font-size: 11px;
  line-height: 1.4;
  background: ${({ $active }) =>
    $active ? "var(--soremo-blue)" : "transparent"};
  color: ${({ $active }) => ($active ? "#ffffff" : "var(--soremo-light-gray)")};
  transition:
    background 0.15s ease,
    color 0.15s ease;

  &:not(:last-child) {
    border-right: 1px solid var(--soremo-border);
  }
`;

// AI 支援のラベル（チクチクフィルター / 返信君）
const AiAssistLabel = styled.span.attrs({
  className: "label8",
})`
  color: var(--soremo-light-gray);
  display: inline-flex;
  align-items: center;
  gap: 2px;

  & > .material-symbols-rounded {
    font-size: 14px;
    color: var(--soremo-blue);
  }
`;

const CommentField: React.FC<CommentFieldProps> = ({
  ref,
  showPinIcon = true,
  showAttachIcon = true,
  onSend = () => {},
  showBottomAppBar = true,
  scrollEnabled = true,
  editMode = false,
  initialValue = "",
  onCancel,
  replyMode = false,
  replyToMessage = null,
  onFilesSelected,
  onFilesDroppedWithPath,
  selectedFiles = [],
  onRemoveFile,
  acceptFileTypes,
  multipleFiles = true,
  onPinClick,
  isPinActive = false,
  pinTooltip,
  getLiveTimecode,
  showIconsInEditMode = false,
  existingFiles = [],
  onRemoveExistingFile,
  placeholder: customPlaceholder = "メッセージを送信",
  onTypingChange,
  onTypingCommit,
  enableChikuchiku = false,
  chikuchikuScene = "chikuchiku_filter",
  chikuchikuContext,
  initialProofread = null,
  enableReplyAssistant = false,
  replyAssistantScene = "reply_assistant_shot",
  replyAssistantContext,
  allowEmptySend = false,
}) => {
  const [message, setMessage] = useState(initialValue);
  const [showMarkdownHelp, setShowMarkdownHelp] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 外部（Shift+M ショートカット等）から入力欄を操作するための handle。
  // withPin 時の挿入処理はピンアイコンの onClick と同じ挙動に揃える。
  useImperativeHandle(
    ref,
    () => ({
      focusComposer: (options?: { withPin?: boolean }) => {
        if (options?.withPin && !isPinActive) {
          const result = onPinClick?.();
          if (typeof result === "string") {
            setMessage((prev) => result + (prev ? " " + prev : ""));
          }
        }
        // 挿入による再レンダリング後にフォーカスし、カーソルを末尾へ移動する
        setTimeout(() => {
          const el = textareaRef.current;
          if (!el) return;
          el.focus();
          const len = el.value.length;
          el.setSelectionRange(len, len);
        }, 0);
      },
    }),
    [isPinActive, onPinClick],
  );

  // ===== ライブタイムコード（タイムラインモードのピン常時 ON） =====
  // getLiveTimecode 指定時のみ有効。再生位置を一定間隔でポーリングして
  // チップに表示し、入力開始時点の値を frozenTimecode として凍結する。
  const timecodeTrackingEnabled = !!getLiveTimecode;
  const [liveTimecode, setLiveTimecode] = useState<string | null>(null);
  const [frozenTimecode, setFrozenTimecode] = useState<string | null>(null);

  useEffect(() => {
    if (!getLiveTimecode) {
      setLiveTimecode(null);
      setFrozenTimecode(null);
      return;
    }
    const update = () => setLiveTimecode(getLiveTimecode());
    update();
    const timer = window.setInterval(update, LIVE_TIMECODE_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [getLiveTimecode]);

  /** チップに表示するタイムコード（凍結中はその値、それ以外は再生位置追従）。 */
  const displayedTimecode = frozenTimecode ?? liveTimecode;

  // AI 支援（チクチクフィルター / 返信君）の状態。両機能で共有する。
  // aiOriginal / aiCorrected は「現在アクティブでない側」のスナップショットを保持し、
  // アクティブ側の最新テキストは常に `message` が持つ。
  // - チクチク: aiOriginal=元の文 / aiCorrected=校正案
  // - 返信君  : aiOriginal=自分の文 / aiCorrected=返信案
  const [aiOriginal, setAiOriginal] = useState<string | null>(null);
  const [aiCorrected, setAiCorrected] = useState<string | null>(null);
  const [displayVariant, setDisplayVariant] = useState<
    "original" | "corrected"
  >("original");
  const [transformText, { isLoading: isProofreading }] =
    useTransformTextMutation();

  const showHelpIcon = (!editMode || showIconsInEditMode) && !replyMode;

  // 添付が有効な状態。添付ボタンの表示条件と、貼り付け / ドロップの受付条件を共有する
  const attachEnabled =
    showAttachIcon && (!editMode || showIconsInEditMode) && !replyMode;

  // Reduxからスクロール状態を取得
  const scrollState = useSelector((state: RootState) => state.layout);

  // Reduxからルート情報を取得してGlobalMenuの表示状態を判断
  const currentRoute = useSelector(
    (state: RootState) => state.router?.location?.pathname,
  );
  const routes = useSelector((state: RootState) => state.router?.routes);

  // 現在のルートに基づいてGlobalMenuの表示状態を判断
  const isHideTopBar = useCallback(() => {
    if (!routes || !currentRoute) return false;

    // 現在のルートに一致するルート設定を探す
    const matchedRoute = routes.find(
      (route) =>
        currentRoute.startsWith(route.path) || currentRoute === route.path,
    );

    return matchedRoute?.isHideTopBar || false;
  }, [currentRoute, routes]);

  // 実際のGlobalMenuの表示状態
  const globalMenuVisible = !isHideTopBar();

  // スクロール状態を考慮したGlobalMenuの表示状態
  const globalMenuActuallyVisible = globalMenuVisible && scrollState !== "down";

  // 編集モード時に初期値を設定（AI 校正の両バージョンがあれば復元）
  useEffect(() => {
    if (!editMode) return;
    if (
      initialProofread &&
      (initialProofread.original || initialProofread.corrected)
    ) {
      const activeIsCorrected = initialProofread.active === "ai";
      setAiOriginal(initialProofread.original ?? "");
      setAiCorrected(initialProofread.corrected ?? "");
      setDisplayVariant(activeIsCorrected ? "corrected" : "original");
      setMessage(
        activeIsCorrected
          ? (initialProofread.corrected ?? "")
          : (initialProofread.original ?? ""),
      );
    } else {
      // 校正情報を持たないコメントの編集時は、前回ドラフトの校正状態を持ち越さない。
      setAiOriginal(null);
      setAiCorrected(null);
      setDisplayVariant("original");
      if (initialValue !== undefined) {
        setMessage(initialValue);
      }
    }
  }, [editMode, initialValue, initialProofread]);

  // AI 支援（チクチク / 返信君）の状態をリセットする
  const resetAiAssist = useCallback(() => {
    setAiOriginal(null);
    setAiCorrected(null);
    setDisplayVariant("original");
  }, []);

  // チクチクフィルターを実行（入力テキストを校正案に変換）
  const handleChikuchiku = useCallback(async () => {
    if (isProofreading) return;
    const projectId = chikuchikuContext?.project_id;
    if (!projectId) return;
    // 校正のベースは「元の文」。すでに校正済みで元側を編集している場合は最新の message を使う。
    const base = (
      displayVariant === "original" ? message : (aiOriginal ?? message)
    ).trim();
    if (!base) return;

    try {
      const res = await transformText({
        scene: chikuchikuScene,
        text: base,
        project_id: projectId,
        shot_id: chikuchikuContext?.shot_id ?? undefined,
        dm_id: chikuchikuContext?.dm_id ?? undefined,
        chat_id: chikuchikuContext?.chat_id ?? undefined,
        exclude_comment_id: chikuchikuContext?.exclude_comment_id ?? undefined,
      }).unwrap();
      const corrected = res?.data?.transformed_text ?? "";
      if (!corrected) {
        toast.error("校正結果を取得できませんでした");
        return;
      }
      setAiOriginal(base);
      setAiCorrected(corrected);
      setDisplayVariant("corrected");
      setMessage(corrected);
      setTimeout(() => textareaRef.current?.focus(), 0);
    } catch (e: unknown) {
      const errorMessage =
        (e as { data?: { message?: string } })?.data?.message ||
        "チクチクフィルターの実行に失敗しました";
      toast.error(errorMessage);
    }
  }, [
    isProofreading,
    chikuchikuContext,
    displayVariant,
    message,
    aiOriginal,
    transformText,
    chikuchikuScene,
  ]);

  // 返信君を実行（相手の履歴から返信案を生成）
  // 下書きが空の場合は生成結果をそのまま挿入し、下書きがある場合は
  // 「自分の文 / 返信案」のトグルを用意する。
  const handleReplyAssistant = useCallback(async () => {
    if (isProofreading) return;
    const projectId = replyAssistantContext?.project_id;
    if (!projectId) return;
    // 下書きのベースは「自分の文」側。すでに生成済みで自分の文側を編集している場合は最新の message を使う。
    const draft = (
      displayVariant === "original" ? message : (aiOriginal ?? message)
    ).trim();

    try {
      const res = await transformText({
        scene: replyAssistantScene,
        text: draft,
        project_id: projectId,
        shot_id: replyAssistantContext?.shot_id ?? undefined,
        dm_id: replyAssistantContext?.dm_id ?? undefined,
        chat_id: replyAssistantContext?.chat_id ?? undefined,
        exclude_comment_id:
          replyAssistantContext?.exclude_comment_id ?? undefined,
      }).unwrap();
      const generated = res?.data?.transformed_text ?? "";
      if (!generated) {
        toast.error("返信案を取得できませんでした");
        return;
      }
      if (draft) {
        // 下書きがある場合は「自分の文 / 返信案」のトグルを用意。
        setAiOriginal(draft);
        setAiCorrected(generated);
        setDisplayVariant("corrected");
      } else {
        // 下書きが空の場合は生成結果をそのまま挿入（トグル・proofread なし）。
        resetAiAssist();
      }
      setMessage(generated);
      setTimeout(() => textareaRef.current?.focus(), 0);
    } catch (e: unknown) {
      const errorMessage =
        (e as { data?: { message?: string } })?.data?.message ||
        "返信君の実行に失敗しました";
      toast.error(errorMessage);
    }
  }, [
    isProofreading,
    replyAssistantContext,
    displayVariant,
    message,
    aiOriginal,
    transformText,
    replyAssistantScene,
    resetAiAssist,
  ]);

  // 元/校正のトグル切り替え（切り替え前にアクティブ側の編集を保存）
  const switchVariant = useCallback(
    (target: "original" | "corrected") => {
      if (target === displayVariant) return;
      if (displayVariant === "corrected") {
        setAiCorrected(message);
        setMessage(aiOriginal ?? "");
      } else {
        setAiOriginal(message);
        setMessage(aiCorrected ?? "");
      }
      setDisplayVariant(target);
      setTimeout(() => textareaRef.current?.focus(), 0);
    },
    [displayVariant, message, aiOriginal, aiCorrected],
  );

  // チクチクフィルター行を表示するか（入力がある or 校正案がある場合）。
  // 引用返信（replyMode）時も利用できるよう、replyMode では分岐しない。
  const showChikuchikuBar =
    enableChikuchiku && (message.trim() !== "" || aiCorrected !== null);
  // 返信君行は入力が空でも表示する（下書き無しでも生成できるため）。
  // 引用返信（replyMode）時も同様に表示する。
  const showReplyBar = enableReplyAssistant;
  // AI 支援バー（チクチク or 返信君）を表示するか
  const showAiBar = showChikuchikuBar || showReplyBar;
  // 返信君（返信生成）モードか校正モードかで、バーのラベル・トグルの文言を切り替える。
  // ※ `replyMode`（メッセージ引用返信）とは別概念であることに注意。
  const isReplyAssistant = enableReplyAssistant;
  const aiBarLabel = isReplyAssistant ? "返信君" : "チクチクフィルター";
  const aiBarIcon = isReplyAssistant ? "quickreply" : "auto_fix_high";
  const originalVariantLabel = isReplyAssistant ? "自分の文" : "元の文";
  const correctedVariantLabel = isReplyAssistant ? "返信案" : "校正案";
  const runAiTransform = isReplyAssistant
    ? handleReplyAssistant
    : handleChikuchiku;

  // テキストエリアのスクロールイベントハンドラ
  const handleTextareaWheel = useCallback((e: WheelEvent) => {
    // イベントの伝播を停止しない（GlobalWrapperのスクロール検出が機能するため）
    e.stopPropagation = () => {};
  }, []);

  // テキストエリアの最大高さを計算する関数
  const calculateMaxHeight = useCallback(() => {
    // ウィンドウの高さを取得
    const windowHeight = window.innerHeight;

    // 基本の最大高さ（利用可能な空間をフルに使用）
    let maxHeight = windowHeight;

    // 常に差し引く高さ（固定要素）
    maxHeight -=
      CONTAINER_PADDING +
      CONTAINER_BORDER +
      BUTTON_AREA_HEIGHT +
      PROJECT_BANNER_HEIGHT;

    // スクロール状態に応じた高さ調整
    if (!(scrollEnabled && scrollState === "down")) {
      // GlobalMenuが表示されている場合、その高さを考慮
      if (globalMenuActuallyVisible) {
        maxHeight -= GLOBAL_MENU_HEIGHT;
      }

      // BottomAppBarが表示されている場合、その高さを考慮
      if (showBottomAppBar) {
        maxHeight -= BOTTOM_APP_BAR_HEIGHT;
      }
    }

    // 最大高さが最小値より小さい場合は最小値を設定
    return Math.max(maxHeight, MIN_TEXTAREA_HEIGHT);
  }, [showBottomAppBar, globalMenuActuallyVisible, scrollEnabled, scrollState]);

  // コンポーネントマウント時にテキストエリアにホイールイベントリスナーを追加
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.addEventListener("wheel", handleTextareaWheel, {
        passive: true,
      });

      return () => {
        textarea.removeEventListener("wheel", handleTextareaWheel);
      };
    }
  }, [handleTextareaWheel]);

  // ウィンドウサイズ変更時に最大高さを更新
  useEffect(() => {
    const updateMaxHeight = () => {
      if (textareaRef.current) {
        textareaRef.current.style.maxHeight = `${calculateMaxHeight()}px`;
      }
    };

    // 初期設定
    updateMaxHeight();

    // リサイズイベントリスナーを追加
    window.addEventListener("resize", updateMaxHeight);

    // クリーンアップ関数
    return () => {
      window.removeEventListener("resize", updateMaxHeight);
    };
  }, [calculateMaxHeight]);

  // テキストエリアの高さを自動調整する関数
  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // 一度高さをリセットして正確なスクロール高さを取得
    textarea.style.height = "auto";

    // スクロール高さに基づいて高さを計算（最大高さを超えないように）
    let newHeight = Math.min(textarea.scrollHeight, calculateMaxHeight());

    // 高さを行の高さの倍数に調整
    // 端数が出た場合は切り上げて次の行の高さに合わせる
    const lineCount = Math.ceil(newHeight / LINE_HEIGHT_PX);
    newHeight = lineCount * LINE_HEIGHT_PX;

    // 最大高さを超えないように再調整
    newHeight = Math.min(newHeight, calculateMaxHeight());

    textarea.style.height = `${newHeight}px`;

    // スクロールが必要かどうかを判断し、クラスを追加/削除
    const needsScroll = textarea.scrollHeight > newHeight;
    if (needsScroll) {
      textarea.classList.add("needs-scroll");
    } else {
      textarea.classList.remove("needs-scroll");
    }

    // モバイル表示かどうかを判断
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      // モバイル表示時は、スクロールが必要ない場合はoverflow-yをhiddenに設定
      textarea.style.overflowY = needsScroll ? "auto" : "hidden";
    }
  }, [calculateMaxHeight]);

  // 初期レンダリング時に高さを調整
  useEffect(() => {
    adjustHeight();
  }, [adjustHeight]);

  // メッセージが変更されたときに高さを調整
  useEffect(() => {
    adjustHeight();
  }, [adjustHeight, message]);

  // 添付した画像のサムネイル用 object URL（selectedFiles と同じ index で対応）。
  // 依存にファイルの実体シグネチャを使うのは、selectedFiles の配列参照が毎レンダー
  // 変わる呼び出し元でも URL を作り直さないようにするため。
  const [imagePreviewUrls, setImagePreviewUrls] = useState<(string | null)[]>(
    [],
  );
  const selectedFilesSignature = selectedFiles
    .map((file) => `${file.name}:${file.size}:${file.lastModified}`)
    .join("|");

  useEffect(() => {
    const urls = selectedFiles.map((file) =>
      file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
    );
    setImagePreviewUrls(urls);
    return () => {
      urls.forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilesSignature]);

  // テキストエリア自体の width 変化時にも高さを再計算する。
  // 親レイアウトが初回レンダリング時に width=0 で描画されるケース（例: DM の
  // DmActionPanel は ResizeObserver で panelRect.width を後追いで確定させる）、
  // scrollHeight がプレースホルダ1文字ずつの折り返しで巨大化し、そのまま
  // 高さが固定される問題を防ぐ。
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    let lastWidth = textarea.clientWidth;
    const ro = new ResizeObserver(() => {
      const currentWidth = textarea.clientWidth;
      if (currentWidth !== lastWidth) {
        lastWidth = currentWidth;
        adjustHeight();
      }
    });
    ro.observe(textarea);
    return () => ro.disconnect();
  }, [adjustHeight]);

  /** 音声入力の挿入状態。null なら停止中。 */
  const dictationRef = useRef<DictationInsertion | null>(null);

  // 一度もフォーカスしていない textarea の selectionStart は 0 のままなので、
  // 音声入力の挿入位置を「カーソル位置」と「末尾」で切り替えるために使う。
  const hasFocusedTextareaRef = useRef(false);

  /** 本文差し替えの共通処理。手入力と音声入力の両方から呼ぶ。 */
  const applyMessageChange = (next: string) => {
    // ライブタイムコード: 入力開始でその時点の再生位置を凍結し、
    // 全消しで追従に戻す（書いている間に時間がずれるのを防ぐ）。
    if (timecodeTrackingEnabled) {
      if (
        next.trim() !== "" &&
        message.trim() === "" &&
        frozenTimecode === null
      ) {
        setFrozenTimecode(liveTimecode);
      } else if (next.trim() === "") {
        setFrozenTimecode(null);
      }
    }
    setMessage(next);
    onTypingChange?.();
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value;
    // 音声入力中の手入力は、以降の認識結果で上書きされないよう挿入位置を取り直す
    if (dictationRef.current) {
      dictationRef.current = {
        base: next,
        offset: e.target.selectionStart ?? next.length,
      };
    }
    applyMessageChange(next);
  };

  const {
    isSupported: isDictationSupported,
    isListening: isDictating,
    start: startDictation,
    stop: stopDictation,
  } = useSpeechRecognition({
    onResult: (finalChunk, interim) => {
      const dictation = dictationRef.current;
      if (!dictation) return;

      if (finalChunk) {
        dictation.base =
          dictation.base.slice(0, dictation.offset) +
          finalChunk +
          dictation.base.slice(dictation.offset);
        dictation.offset += finalChunk.length;
      }

      applyMessageChange(
        dictation.base.slice(0, dictation.offset) +
          interim +
          dictation.base.slice(dictation.offset),
      );
    },
    onError: (reason) => {
      const description = DICTATION_ERROR_MESSAGES[reason];
      if (description) toast.error(description);
    },
  });

  const handleDictationToggle = () => {
    if (isDictating) {
      stopDictation();
    } else {
      // 開始時のカーソル位置を挿入位置として覚える。マイクを押した時点で
      // textarea は blur しているが selectionStart は直前の位置を保つ。
      // 一度も触っていない場合は selectionStart が 0 のままなので末尾に挿入する。
      const caret = textareaRef.current?.selectionStart;
      dictationRef.current = {
        base: message,
        offset:
          hasFocusedTextareaRef.current && caret !== undefined
            ? caret
            : message.length,
      };
      startDictation();
    }
    textareaRef.current?.focus();
  };

  /**
   * 音声入力を終了して挿入状態を捨てる（送信 / キャンセル時）。
   * 本文をクリアした後に認識結果が届いて古い文が戻るのを防ぐ。
   */
  const endDictation = useCallback(() => {
    dictationRef.current = null;
    stopDictation();
  }, [stopDictation]);

  // 音声入力は本文入力の補助なので、返信モードでも出す（Firefox 等の未対応環境では非表示）
  const showDictationIcon =
    isDictationSupported && (!editMode || showIconsInEditMode);
  const dictationLabel = isDictating ? "音声入力を停止" : "音声入力を開始";

  const hasContent =
    message.trim() !== "" ||
    selectedFiles.length > 0 ||
    existingFiles.length > 0 ||
    allowEmptySend;

  /**
   * 送信本文を組み立てる。ライブタイムコードモード（タイムラインの
   * ピン常時 ON）では、本文先頭にタイムコードが無ければチップの値を
   * 自動付与する（編集モードでは既存本文を尊重して付与しない）。
   */
  const buildOutgoingMessage = (): string => {
    if (!timecodeTrackingEnabled || editMode) return message;
    const timecode = frozenTimecode ?? liveTimecode;
    if (!timecode) return message;
    if (BRACKET_TIMECODE_HEAD_RE.test(message)) return message;
    const trimmed = message.trim();
    return trimmed ? `${timecode} ${trimmed}` : timecode;
  };

  const handleSend = () => {
    if (!hasContent) return;
    endDictation();
    const outgoing = buildOutgoingMessage();
    // チクチクフィルターで校正案を生成済みの場合は、元/校正の両バージョンを送る。
    let result: void | boolean | Promise<void | boolean>;
    if (aiCorrected !== null) {
      const original =
        displayVariant === "corrected" ? (aiOriginal ?? "") : message;
      const corrected =
        displayVariant === "corrected" ? message : (aiCorrected ?? "");
      result = onSend(outgoing, {
        original_comment: original,
        ai_comment: corrected,
        active_variant: displayVariant === "corrected" ? "ai" : "original",
      });
    } else {
      result = onSend(outgoing);
    }
    // 送信ハンドラが false を返した / 解決した場合（アップロード失敗ダイアログの
    // キャンセル等）は、ユーザーが再入力していなければ本文を復元する
    void Promise.resolve(result)
      .then((ok) => {
        if (ok === false) {
          setMessage((prev) => (prev === "" ? outgoing : prev));
        }
      })
      .catch(() => {});
    setMessage("");
    setFrozenTimecode(null);
    resetAiAssist();
    onTypingCommit?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== "Enter") return;

    // ⌘/Ctrl+Enter で送信。素の Enter は従来どおり改行のままにする。
    // IME 変換中（isComposing）は確定操作と区別できないため受け付けない。
    if ((e.metaKey || e.ctrlKey) && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
      return;
    }

    setTimeout(adjustHeight, 0);
  };

  // フォーカス時のイベントハンドラ
  const handleFocus = useCallback(() => {
    hasFocusedTextareaRef.current = true;
    adjustHeight();

    // モバイル表示時は、仮想キーボードが表示されるため、少し遅延させて再度高さを調整
    setTimeout(() => {
      adjustHeight();
    }, 300);
  }, [adjustHeight]);

  // ブラー時のイベントハンドラ
  const handleBlur = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // モバイル表示時は、スクロールバーを非表示にする
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      textarea.style.overflowY = "hidden";
    }

    // blur で typing.*.stop を確定送信（BE 仕様書 §4.1）。
    onTypingCommit?.();
  }, [onTypingCommit]);

  // キャンセルハンドラー
  const handleCancel = useCallback(() => {
    endDictation();
    setMessage("");
    setFrozenTimecode(null);
    resetAiAssist();
    onCancel?.();
  }, [endDictation, onCancel, resetAiAssist]);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        const fileArray = Array.from(files);
        onFilesSelected?.(fileArray);
      }
      // inputの値をリセット（同じファイルを再選択可能にする）
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [onFilesSelected],
  );

  /**
   * クリップボードからの貼り付けで添付する（スクリーンショット画像、
   * Finder / エクスプローラーでコピーしたファイル）。
   *
   * Excel や Word はテキストと画像を同時にクリップボードへ載せるため、
   * テキストを含む場合は従来どおりテキストの貼り付けを優先して添付しない。
   */
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      if (!attachEnabled || !onFilesSelected) return;

      const files = Array.from(e.clipboardData.files);
      if (files.length === 0) return;
      if (e.clipboardData.getData("text/plain").trim() !== "") return;

      e.preventDefault();
      const targets = multipleFiles ? files : files.slice(0, 1);
      onFilesSelected(targets.map(withPastedFileName));
    },
    [attachEnabled, multipleFiles, onFilesSelected],
  );

  // ===== ドラッグ&ドロップ添付 =====
  const dropEnabled = attachEnabled && !!onFilesSelected;
  const { ref: containerRef, isDragging } = useFileDropTarget({
    enabled: dropEnabled,
    multiple: multipleFiles,
    onFiles: (files) => onFilesSelected?.(files),
    onFilesWithPath: onFilesDroppedWithPath,
  });

  // ハイライトはポータル先で position: fixed なので、入力欄の現在位置を測って渡す。
  // フェードアウト中も位置が要るため、消えるときは最後の矩形を残す。
  const [highlightRect, setHighlightRect] = useState<DOMRect | null>(null);
  useLayoutEffect(() => {
    if (!isDragging) return;
    setHighlightRect(containerRef.current?.getBoundingClientRect() ?? null);
  }, [isDragging, containerRef]);

  return (
    <>
      {dropEnabled &&
        highlightRect &&
        createPortal(
          <DropHighlight
            $active={isDragging}
            aria-hidden="true"
            style={{
              top: highlightRect.top,
              left: highlightRect.left,
              width: highlightRect.width,
              height: highlightRect.height,
            }}
          >
            <DropHighlightIcon>file_upload</DropHighlightIcon>
            <DropHighlightText>ドロップでここに添付</DropHighlightText>
          </DropHighlight>,
          document.body,
        )}
      <CommentFieldContainer ref={containerRef}>
        {/* 編集モード時のヘッダー */}
        {editMode && (
          <div className="u-row-between u-w100">
            <EditModeLabel>編集中...</EditModeLabel>
            <IconButton
              onClick={handleCancel}
              style={{ fontSize: "24px" }}
              icon="close"
            />
          </div>
        )}
        {/* 返信モード時のヘッダー */}
        {replyMode && replyToMessage && (
          <div className="u-col u-gap8 u-w100">
            <div className="u-row-between u-w100">
              <ReplyModeLabel>返信中...</ReplyModeLabel>
              <IconButton
                onClick={handleCancel}
                style={{ fontSize: "24px" }}
                icon="close"
              />
            </div>
            <ReplyPreviewContainer>
              <ReplyAuthorName>
                {replyToMessage.author.display_name}
              </ReplyAuthorName>
              <ReplyMessagePreview>
                {replyToMessage.message}
              </ReplyMessagePreview>
            </ReplyPreviewContainer>
          </div>
        )}
        {/* 既存添付ファイルプレビュー（編集時） */}
        {existingFiles.length > 0 && (
          <SelectedFilesContainer>
            {existingFiles.map((file) => (
              <SelectedFileItem key={file.id}>
                <FileIcon>
                  {getFileIconFromCategoryOrExtension(
                    file.fileType,
                    file.fileName,
                  )}
                </FileIcon>
                <ToolTip description={file.fileName}>
                  <SelectedFileName>{file.fileName}</SelectedFileName>
                </ToolTip>
                {onRemoveExistingFile && (
                  <RemoveFileButton
                    type="button"
                    onClick={() => onRemoveExistingFile(file.id)}
                    aria-label={`${file.fileName}を削除`}
                  >
                    <RemoveFileIcon>close</RemoveFileIcon>
                  </RemoveFileButton>
                )}
              </SelectedFileItem>
            ))}
          </SelectedFilesContainer>
        )}
        {/* 新規選択ファイルプレビュー */}
        {selectedFiles.length > 0 && (
          <SelectedFilesContainer>
            {selectedFiles.map((file, index) => (
              <SelectedFileItem key={`${file.name}-${index}`}>
                {imagePreviewUrls[index] ? (
                  <FileThumbnail src={imagePreviewUrls[index]} alt="" />
                ) : (
                  <FileIcon>{getFileIconFromMime(file.type)}</FileIcon>
                )}
                <ToolTip description={file.name}>
                  <SelectedFileName>{file.name}</SelectedFileName>
                </ToolTip>
                <RemoveFileButton
                  type="button"
                  onClick={() => onRemoveFile?.(index)}
                  aria-label={`${file.name}を削除`}
                >
                  <RemoveFileIcon>close</RemoveFileIcon>
                </RemoveFileButton>
              </SelectedFileItem>
            ))}
          </SelectedFilesContainer>
        )}
        {/* AI 支援行（チクチクフィルター / 返信君。テキストエリア直上） */}
        {showAiBar && (
          <AiAssistBar>
            {aiCorrected === null ? (
              isReplyAssistant ? (
                <ToolTip
                  description="返信案を作成"
                  placement="top"
                  appendTo="parent"
                >
                  <AiAssistTrigger
                    type="button"
                    onClick={handleReplyAssistant}
                    disabled={isProofreading}
                  >
                    <span className="material-symbols-rounded">
                      {aiBarIcon}
                    </span>
                    {isProofreading ? "作成中…" : aiBarLabel}
                  </AiAssistTrigger>
                </ToolTip>
              ) : (
                <ToolTip
                  description="コメントを校正"
                  placement="top"
                  appendTo="parent"
                >
                  <AiAssistTrigger
                    type="button"
                    onClick={handleChikuchiku}
                    disabled={isProofreading || message.trim() === ""}
                  >
                    <span className="material-symbols-rounded">
                      {aiBarIcon}
                    </span>
                    {isProofreading ? "校正中…" : aiBarLabel}
                  </AiAssistTrigger>
                </ToolTip>
              )
            ) : (
              <>
                <AiAssistLabel>
                  <span className="material-symbols-rounded">{aiBarIcon}</span>
                  {aiBarLabel}
                </AiAssistLabel>
                <VariantToggle
                  role="group"
                  aria-label={`${originalVariantLabel}と${correctedVariantLabel}の切り替え`}
                >
                  <VariantButton
                    type="button"
                    $active={displayVariant === "original"}
                    onClick={() => switchVariant("original")}
                  >
                    {originalVariantLabel}
                  </VariantButton>
                  <VariantButton
                    type="button"
                    $active={displayVariant === "corrected"}
                    onClick={() => switchVariant("corrected")}
                  >
                    {correctedVariantLabel}
                  </VariantButton>
                </VariantToggle>
                <ToolTip
                  description={
                    isReplyAssistant ? "返信案を作り直す" : "校正をやり直す"
                  }
                  placement="top"
                  appendTo="parent"
                >
                  <IconButton
                    icon="refresh"
                    onClick={runAiTransform}
                    disabled={isProofreading}
                    size={20}
                    color="var(--soremo-light-gray)"
                  />
                </ToolTip>
              </>
            )}
          </AiAssistBar>
        )}
        <StyledTextarea
          ref={textareaRef}
          className="comment-textarea"
          placeholder={
            editMode
              ? "メッセージを編集"
              : replyMode
                ? "返信を入力"
                : customPlaceholder
          }
          value={message}
          onChange={handleMessageChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={{
            maxHeight: `${calculateMaxHeight()}px`,
            minHeight: `${LINE_HEIGHT_PX}px`,
          }}
          rows={1}
        />
        <div className="u-w100 u-row-between">
          <div className="u-row u-gap8">
            {showPinIcon &&
              (!editMode || showIconsInEditMode) &&
              !replyMode && (
                <ToolTip
                  description={pinTooltip || ""}
                  placement="top"
                  appendTo="parent"
                >
                  <IconButton
                    onClick={() => {
                      const result = onPinClick?.();
                      if (typeof result === "string") {
                        setMessage((prev) => result + (prev ? " " + prev : ""));
                        setTimeout(() => {
                          if (textareaRef.current) {
                            textareaRef.current.focus();
                          }
                        }, 0);
                      }
                    }}
                    style={{ color: isPinActive ? "#009ace" : undefined }}
                    icon="pin_drop"
                  />
                </ToolTip>
              )}
            {timecodeTrackingEnabled && displayedTimecode && (
              <ToolTip
                description={
                  frozenTimecode !== null
                    ? "この位置に固定中"
                    : "再生位置に追従中"
                }
                placement="top"
                appendTo="parent"
              >
                <TimecodeChip
                  $frozen={frozenTimecode !== null}
                  role={frozenTimecode !== null ? "button" : undefined}
                  onClick={() => {
                    // 凍結中のみ、クリックで現在の再生位置へ取り直す
                    // （追従中は再生位置そのものなので操作不要）。
                    if (frozenTimecode !== null) {
                      setFrozenTimecode(getLiveTimecode?.() ?? liveTimecode);
                    }
                  }}
                >
                  {displayedTimecode}
                </TimecodeChip>
              </ToolTip>
            )}
            {attachEnabled && (
              <>
                <ToolTip
                  description="ファイルを添付"
                  placement="top"
                  appendTo="parent"
                >
                  <IconButton
                    onClick={() => fileInputRef.current?.click()}
                    icon="add_circle"
                  />
                </ToolTip>
                <HiddenFileInput
                  ref={fileInputRef}
                  type="file"
                  accept={acceptFileTypes}
                  multiple={multipleFiles}
                  onChange={handleFileChange}
                />
              </>
            )}
            {showDictationIcon && (
              <ToolTip
                description={
                  isDictating ? "音声入力を停止" : "音声入力"
                }
                placement="top"
                appendTo="parent"
              >
                <DictationIndicator $active={isDictating}>
                  <IconButton
                    icon={isDictating ? "mic" : "mic_none"}
                    onClick={handleDictationToggle}
                    filled={isDictating}
                    color={
                      isDictating
                        ? "var(--soremo-blue)"
                        : "var(--soremo-light-gray)"
                    }
                    ariaLabel={dictationLabel}
                  />
                </DictationIndicator>
              </ToolTip>
            )}
            {showHelpIcon && (
              <ToolTip
                description="マークダウン早見表"
                placement="top"
                appendTo="parent"
              >
                <IconButton
                  icon="help"
                  onClick={() => setShowMarkdownHelp(true)}
                  color="var(--soremo-border)"
                  hoverColor="var(--soremo-light-gray)"
                />
              </ToolTip>
            )}
          </div>
          <div className="u-row u-gap8">
            <ToolTip
              description={`送信（${SEND_SHORTCUT_LABEL}）`}
              placement="top"
              appendTo="parent"
            >
              <IconButton
                icon="send"
                onClick={handleSend}
                disabled={!hasContent}
                size={32}
                color={
                  hasContent ? "var(--soremo-blue)" : "var(--soremo-border)"
                }
                hoverColor={
                  hasContent ? "var(--soremo-blue)" : "var(--soremo-border)"
                }
              />
            </ToolTip>
          </div>
        </div>
      </CommentFieldContainer>
      {showMarkdownHelp && (
        <MarkdownHelpModal onClose={() => setShowMarkdownHelp(false)} />
      )}
    </>
  );
};

export default CommentField;
